'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AverageReturnCalculator() {
  const t = useTranslations("tools.averageReturnCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'cashflow' | 'returns'>('cashflow');

  // Cash Flow Based Calculator State
  const [startBalance, setStartBalance] = useState(5600);
  const [startDate, setStartDate] = useState('2022-01-01');
  const [endBalance, setEndBalance] = useState(18000);
  const [endDate, setEndDate] = useState('2025-12-16');
  const [transactions, setTransactions] = useState([
    { type: 'deposit', amount: 5000, date: '2023-01-15' },
    { type: 'withdraw', amount: 1500, date: '2023-06-01' },
    { type: 'deposit', amount: 3800, date: '2024-01-18' }
  ]);
  const [cashflowResults, setCashflowResults] = useState<any>(null);

  // Multiple Returns Calculator State
  const [returnPeriods, setReturnPeriods] = useState([
    { rate: 10, period: 1 },
    { rate: 15, period: 2 },
    { rate: -5, period: 1 }
  ]);
  const [returnsResults, setReturnsResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#eff6ff',
    green: '#10b981',
    greenLight: '#f0fdf4',
    orange: '#f59e0b',
    gray: '#6b7280',
    grayLight: '#f9fafb',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    toolHeader: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        textAlign: 'center' as const,
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorTabs: {
      display: 'flex',
      borderBottom: `2px solid #e5e7eb`,
      background: colors.grayLight,
    },
    tabBtn: (isActive: boolean) => getResponsiveStyle(
      {
        flex: 1,
        background: isActive ? 'white' : 'none',
        border: 'none',
        fontWeight: 600,
        color: isActive ? colors.blue : colors.gray,
        cursor: 'pointer',
        transition: 'all 0.3s',
        borderBottom: isActive ? `3px solid ${colors.blue}` : '3px solid transparent',
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    tabContent: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    infoBox: getResponsiveStyle(
      {
        background: colors.blueLight,
        borderRadius: '0.5rem',
        marginBottom: '1.5rem',
        borderLeft: `4px solid ${colors.blue}`,
        color: '#1e40af',
      },
      { padding: '0.75rem', fontSize: '0.85rem' },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      {
        display: 'block',
        marginBottom: '0.5rem',
        fontWeight: 600,
        color: colors.textPrimary,
      },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.65rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    balanceRow: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginBottom: '1rem',
        padding: '1rem',
        background: colors.blueLight,
        borderRadius: '0.5rem',
        border: `2px solid ${colors.blue}`,
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr 1fr' }
    ),
    transactionRow: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        alignItems: 'end' as const,
        marginBottom: '1rem',
        padding: '1rem',
        background: colors.grayLight,
        borderRadius: '0.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '40px 1fr 1fr 1fr' },
      { gridTemplateColumns: '40px 1fr 1fr 1fr' }
    ),
    transactionNumber: {
      fontWeight: 700,
      color: colors.gray,
      paddingTop: '0.75rem',
    },
    addButton: getResponsiveStyle(
      {
        background: '#f3f4f6',
        color: colors.blue,
        border: `2px dashed ${colors.blue}`,
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        width: '100%',
        margin: '1rem 0',
        transition: 'all 0.3s',
      },
      { padding: '0.65rem 1rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    calculateButton: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        fontWeight: 700,
        borderRadius: '0.5rem',
        cursor: 'pointer',
        width: '100%',
        transition: 'all 0.3s',
        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        marginTop: '2rem',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '0.75rem',
        border: `2px solid ${colors.green}`,
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      {
        margin: '0 0 1.5rem 0',
        color: '#065f46',
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
    },
    resultLabel: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
      },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultValue: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.green,
      },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    resultValueLarge: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.green,
      },
      { fontSize: '1.4rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.75rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '1rem',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.75rem',
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      {
        lineHeight: 1.6,
        color: colors.textSecondary,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      {
        lineHeight: 1.8,
        color: colors.textSecondary,
        marginLeft: '1.5rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(255, 240, 245, 0.8) 0%, rgba(255, 250, 240, 0.8) 100%)',
        borderLeft: `4px solid ${colors.primary}`,
        borderRadius: '0.5rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: getResponsiveStyle(
      {
        marginBottom: '1.5rem',
        paddingBottom: '1.5rem',
        borderBottom: '1px solid rgba(255, 90, 112, 0.1)',
      },
      {},
      {},
      {}
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.3s',
    },
  };

  const handleCashflowCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const start = new Date(startDate);
    const end = new Date(endDate);
    const years = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

    let netCashFlow = 0;
    transactions.forEach(t => {
      if (t.amount && t.date) {
        netCashFlow += t.type === 'deposit' ? t.amount : -t.amount;
      }
    });

    const totalReturn = endBalance - startBalance - netCashFlow;
    const totalReturnPct = (totalReturn / (startBalance + netCashFlow / 2)) * 100;

    const avgAnnualReturn = Math.pow((endBalance / (startBalance + netCashFlow)), 1 / years) - 1;
    const avgAnnualReturnPct = avgAnnualReturn * 100;

    setCashflowResults({
      avgReturn: avgAnnualReturnPct.toFixed(2),
      totalReturn: totalReturn.toFixed(2),
      totalReturnPct: totalReturnPct.toFixed(2),
      period: years.toFixed(2),
      netFlow: netCashFlow.toFixed(2),
    });
  };

  const handleReturnsCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const validPeriods = returnPeriods.filter(r => r.rate !== null && r.period > 0);

    if (validPeriods.length === 0) return;

    const arithmeticAvg = validPeriods.reduce((sum, r) => sum + r.rate, 0) / validPeriods.length;

    let cumulativeReturn = 1;
    validPeriods.forEach(r => {
      cumulativeReturn *= Math.pow(1 + r.rate / 100, r.period);
    });

    const totalPeriod = validPeriods.reduce((sum, r) => sum + r.period, 0);
    const geometricAvg = Math.pow(cumulativeReturn, 1 / totalPeriod) - 1;

    const cumulativeReturnPct = (cumulativeReturn - 1) * 100;

    setReturnsResults({
      arithmeticAvg: arithmeticAvg.toFixed(2),
      geometricAvg: (geometricAvg * 100).toFixed(2),
      cumulativeReturn: cumulativeReturnPct.toFixed(2),
      totalPeriod: totalPeriod.toFixed(2),
    });
  };

  const addTransaction = () => {
    setTransactions([...transactions, { type: 'deposit', amount: 0, date: '' }]);
  };

  const updateTransaction = (index: number, field: string, value: any) => {
    const newTransactions = [...transactions];
    newTransactions[index] = { ...newTransactions[index], [field]: value };
    setTransactions(newTransactions);
  };

  const addReturnPeriod = () => {
    setReturnPeriods([...returnPeriods, { rate: 0, period: 1 }]);
  };

  const updateReturnPeriod = (index: number, field: string, value: number) => {
    const newPeriods = [...returnPeriods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };
    setReturnPeriods(newPeriods);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>🧮 {t("toolTitle")}</h2>
          </div>

          <div style={styles.calculatorTabs}>
            <button
              style={styles.tabBtn(activeTab === 'cashflow')}
              onClick={() => setActiveTab('cashflow')}
              onMouseEnter={(e) => {
                if (activeTab !== 'cashflow') {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = colors.blue;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'cashflow') {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = colors.gray;
                }
              }}
            >
              {t("cashflowTab")}
            </button>
            <button
              style={styles.tabBtn(activeTab === 'returns')}
              onClick={() => setActiveTab('returns')}
              onMouseEnter={(e) => {
                if (activeTab !== 'returns') {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = colors.blue;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'returns') {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = colors.gray;
                }
              }}
            >
              {t("returnsTab")}
            </button>
          </div>

          {/* Cash Flow Tab */}
          {activeTab === 'cashflow' && (
            <div style={styles.tabContent}>
              <div style={styles.infoBox}>
                {t("cashflowInfo")}
              </div>

              <form onSubmit={handleCashflowCalculate}>
                <div style={styles.balanceRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("startingBalance")}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={startBalance}
                      onChange={(e) => setStartBalance(parseFloat(e.target.value))}
                      step="0.01"
                      required
                      onFocus={(e) => e.target.style.borderColor = colors.blue}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("startDate")}</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      onFocus={(e) => e.target.style.borderColor = colors.blue}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  {screenSize !== 'mobile' && <div></div>}
                </div>

                <div style={styles.balanceRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("endingBalance")}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={endBalance}
                      onChange={(e) => setEndBalance(parseFloat(e.target.value))}
                      step="0.01"
                      required
                      onFocus={(e) => e.target.style.borderColor = colors.blue}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("endDate")}</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                      onFocus={(e) => e.target.style.borderColor = colors.blue}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  {screenSize !== 'mobile' && <div></div>}
                </div>

                <h4 style={{ margin: '1.5rem 0 1rem', color: colors.textPrimary }}>{t("transactionsTitle")}</h4>

                {transactions.map((transaction, index) => (
                  <div key={index} style={styles.transactionRow}>
                    {screenSize !== 'mobile' && (
                      <div style={styles.transactionNumber}>{index + 1}.</div>
                    )}
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("transactionType")}</label>
                      <select
                        style={styles.input}
                        value={transaction.type}
                        onChange={(e) => updateTransaction(index, 'type', e.target.value)}
                        onFocus={(e) => e.currentTarget.style.borderColor = colors.blue}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                      >
                        <option value="deposit">{t("deposit")}</option>
                        <option value="withdraw">{t("withdraw")}</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("amount")}</label>
                      <input
                        type="number"
                        style={styles.input}
                        value={transaction.amount}
                        onChange={(e) => updateTransaction(index, 'amount', parseFloat(e.target.value))}
                        step="0.01"
                        onFocus={(e) => e.target.style.borderColor = colors.blue}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("date")}</label>
                      <input
                        type="date"
                        style={styles.input}
                        value={transaction.date}
                        onChange={(e) => updateTransaction(index, 'date', e.target.value)}
                        onFocus={(e) => e.target.style.borderColor = colors.blue}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  style={styles.addButton}
                  onClick={addTransaction}
                  onMouseEnter={(e) => e.currentTarget.style.background = colors.blueLight}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  + {t("addTransaction")}
                </button>

                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>

              {cashflowResults && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>📊 {t("resultsTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("avgAnnualReturn")}</span>
                    <span style={styles.resultValueLarge}>{cashflowResults.avgReturn}%</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("totalReturn")}</span>
                    <span style={styles.resultValue}>${cashflowResults.totalReturn} ({cashflowResults.totalReturnPct}%)</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("investmentPeriod")}</span>
                    <span style={styles.resultValue}>{cashflowResults.period} {t("years")}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("netCashFlow")}</span>
                    <span style={styles.resultValue}>${cashflowResults.netFlow}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Multiple Returns Tab */}
          {activeTab === 'returns' && (
            <div style={styles.tabContent}>
              <div style={styles.infoBox}>
                {t("returnsInfo")}
              </div>

              <form onSubmit={handleReturnsCalculate}>
                {returnPeriods.map((period, index) => (
                  <div key={index} style={styles.transactionRow}>
                    {screenSize !== 'mobile' && (
                      <div style={styles.transactionNumber}>{index + 1}.</div>
                    )}
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("returnRate")}</label>
                      <input
                        type="number"
                        style={styles.input}
                        value={period.rate}
                        onChange={(e) => updateReturnPeriod(index, 'rate', parseFloat(e.target.value))}
                        step="0.01"
                        required
                        onFocus={(e) => e.target.style.borderColor = colors.blue}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("holdingPeriod")}</label>
                      <input
                        type="number"
                        style={styles.input}
                        value={period.period}
                        onChange={(e) => updateReturnPeriod(index, 'period', parseFloat(e.target.value))}
                        step="0.01"
                        required
                        onFocus={(e) => e.target.style.borderColor = colors.blue}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  style={styles.addButton}
                  onClick={addReturnPeriod}
                  onMouseEnter={(e) => e.currentTarget.style.background = colors.blueLight}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  + {t("addReturnPeriod")}
                </button>

                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>

              {returnsResults && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>📊 {t("resultsTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("arithmeticAvg")}</span>
                    <span style={styles.resultValueLarge}>{returnsResults.arithmeticAvg}%</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("geometricAvg")}</span>
                    <span style={styles.resultValueLarge}>{returnsResults.geometricAvg}%</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("cumulativeReturn")}</span>
                    <span style={styles.resultValue}>{returnsResults.cumulativeReturn}%</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("totalHoldingPeriod")}</span>
                    <span style={styles.resultValue}>{returnsResults.totalPeriod} {t("years")}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introPara1")}</p>
        <p style={styles.paragraph}>{t("introPara2")}</p>
        <p style={styles.paragraph}>{t("introPara3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <h3 style={styles.h3}>{t("cashflowMethod")}</h3>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("cashflowStep1")}</li>
          <li style={styles.listItem}>{t("cashflowStep2")}</li>
          <li style={styles.listItem}>{t("cashflowStep3")}</li>
          <li style={styles.listItem}>{t("cashflowStep4")}</li>
        </ol>
        <h3 style={styles.h3}>{t("returnsMethod")}</h3>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("returnsStep1")}</li>
          <li style={styles.listItem}>{t("returnsStep2")}</li>
          <li style={styles.listItem}>{t("returnsStep3")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight1")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight2")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight3")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight4")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight5")}</p>
        </div>
      </section>

      {/* Understanding Returns */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("understandingTitle")}</h2>
        <h3 style={styles.h3}>{t("arithmeticVsGeometric")}</h3>
        <p style={styles.paragraph}>{t("arithmeticDesc")}</p>
        <p style={styles.paragraph}>{t("geometricDesc")}</p>

        <h3 style={styles.h3}>{t("twrrMwrrTitle")}</h3>
        <p style={styles.paragraph}>{t("twrrDesc")}</p>
        <p style={styles.paragraph}>{t("mwrrDesc")}</p>

        <h3 style={styles.h3}>{t("bestPracticesTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("practice1")}</li>
          <li style={styles.listItem}>{t("practice2")}</li>
          <li style={styles.listItem}>{t("practice3")}</li>
          <li style={styles.listItem}>{t("practice4")}</li>
          <li style={styles.listItem}>{t("practice5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t("faq1Q")}</h3>
          <p style={styles.paragraph}>{t("faq1A")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t("faq2Q")}</h3>
          <p style={styles.paragraph}>{t("faq2A")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t("faq3Q")}</h3>
          <p style={styles.paragraph}>{t("faq3A")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t("faq4Q")}</h3>
          <p style={styles.paragraph}>{t("faq4A")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t("faq5Q")}</h3>
          <p style={styles.paragraph}>{t("faq5A")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.calculator.net/average-return-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://corporatefinanceinstitute.com/resources/data-science/average-return/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.angelone.in/knowledge-center/online-share-trading/what-is-average-return"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref3")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.finra.org/investors/insights/investment-returns"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}